import type { PaginatedResponse, UseGamesSearchOptions, Game, UseInfiniteGamesSearchOptions } from '@game/types'
import { toPaginatedResponse } from '../utils/paginatedResponse'
import { shallowRef, useQuery, toValue, useApi, whenever, useCoreConfig, useInfiniteQuery, computed } from '#imports'

export const useGames = (gameOptions: UseGamesSearchOptions) => {
    const { site } = useCoreConfig()

    const api = useApi({ service: 'gameConfiguration' })

    const games = shallowRef<Game[]>([])

    const { params = { page: 1, page_size: 15 }, lazy = false, filter = {}, sort = null, enabled = true } = gameOptions
    const key = () => `game:games-${toValue(gameOptions.key)}`

    const query = useQuery<PaginatedResponse<Game[]> | undefined>(async ({ signal }) => {
        // Hidden tiles are excluded unless the caller's filter says otherwise.
        const filterValue = { exclude_hidden_tile_types: true, ...toValue(filter) }
        const res = await api<PaginatedResponse<Game[]>>('/gateway/game-configuration/public/game-configurations/search', {
            method: 'POST',
            signal,
            params: {
                ...toValue(params),
            },
            body: {
                filter: filterValue,
                brand: site.brand,
                ...(sort && { sort })
            }
        })

        return toPaginatedResponse(res)
    }, {
        key,
        enabled,
        lazy,
        server: !lazy,
    })

    if (import.meta.server) {
        if (query.data.value) {
            games.value = games.value.length ? [...new Set([...query.data.value.data, ...games.value])] : query.data.value.data
        }
    }

    if (import.meta.client) {
        whenever(query.data, (res: PaginatedResponse<Game[]>) => {
            games.value = games.value.length ? [...new Set([...res.data, ...games.value])] : res.data
        }, {
            immediate: true,
        })
    }

    return query.enrich({
        games,
    })
}

export const useInfiniteGames = (options: UseInfiniteGamesSearchOptions) => {
    const { site } = useCoreConfig()

    const api = useApi({ service: 'gameConfiguration' })

    const { params = { page: 1, page_size: 15 }, lazy = false, filter = {}, key, initialPageParam = 1, ...queryOptions } = options

    const query = useInfiniteQuery(async ({ pageParam, signal }) => {
        const filterValue = toValue(filter)
        filterValue.exclude_hidden_tile_types = true
        const res = await api<PaginatedResponse<Game[]>>('/gateway/game-configuration/public/game-configurations/search', {
            method: 'POST',
            signal,
            params: {
                ...toValue(params),
                page: pageParam
            },
            body: {
                filter: filterValue,
                brand: site.brand
            }
        })

        return toPaginatedResponse(res)
    }, {
        ...queryOptions,
        key: () => `game:games:${toValue(key)}`,
        lazy,
        server: !lazy,
        initialPageParam,
        getNextPageParam({ lastPage }) {
            if (lastPage.meta.current_page < lastPage.meta.last_page) {
                return lastPage.meta.current_page + 1
            }
            return undefined // No more pages
        },
        queryCache: {
            ttl: 600 // 10 minutes
        }
    })

    const data = computed(() => {
        return {
            ...query.data.value,
            all: query.data.value.pages.flatMap<Game[]>((page) => {
                return page.data.data
            }).flat()
        }
    })

    return query.enrich({
        data
    })
}
